import React, { useState } from "react";
import { motion } from "framer-motion";
import { InView, LineReveal } from "../motion/Reveal";
import { api } from "@/lib/api";
import { Phone, MessageCircle, Send, MapPin, Mail } from "lucide-react";
import { toast } from "sonner";
import { useLang, UI } from "@/lib/lang";

export default function Contact({ brand, serviceArea = [], services = [] }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: t(services[0]?.title) || "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error(t(UI.formRequired));
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      toast.success(t(UI.formSuccess));
      setForm({ name: "", phone: "", email: "", service: form.service, message: "" });
    } catch (err) {
      toast.error(t(UI.formError) + brand?.phone);
    } finally {
      setLoading(false);
    }
  };

  const waMessage = lang === "es"
    ? "Hola El Cuquito, me interesa una cotización."
    : "Hi El Cuquito, I'd like a quote.";
  const waLink = `https://wa.me/1${(brand?.whatsapp || "").replace(/\D/g, "")}?text=${encodeURIComponent(waMessage)}`;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 md:py-40 bg-[#0A0A0A]"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 md:px-14">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-3">
            <p className="num-tick mb-4">{t(UI.section06)}</p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2 className="font-display uppercase text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9]" data-testid="contact-title">
              <LineReveal text={t(UI.contactH1)} />
              <span className="block text-[#1E7FFF] txt-shadow-blue">
                <LineReveal text={t(UI.contactH2)} delay={0.2} />
              </span>
            </h2>
            <p className="mt-8 text-white/60 text-base md:text-lg font-body max-w-2xl">
              {t(UI.contactSub)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-12">
          <div className="col-span-12 md:col-span-5">
            <InView>
              <div className="border-t border-white/10 py-8">
                <p className="num-tick text-white/50 mb-4">{t(UI.contactPhoneLabel)}</p>
                <a
                  href={`tel:${(brand?.phone || "").replace(/\s/g, "")}`}
                  className="font-display text-white text-4xl md:text-6xl lg:text-7xl block leading-none hover:text-[#1E7FFF] transition-colors"
                  data-testid="contact-phone"
                >
                  {brand?.phone}
                </a>
                <div className="mt-3 flex items-center gap-2 text-white/50 text-sm">
                  <Phone size={14} /> {t(UI.contactHours)}
                </div>
              </div>

              <div className="border-t border-white/10 py-8">
                <p className="num-tick text-white/50 mb-4">{t(UI.contactWaLabel)}</p>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-display text-white text-4xl md:text-6xl lg:text-7xl block leading-none hover:text-[#25D366] transition-colors"
                  data-testid="contact-whatsapp"
                >
                  {brand?.whatsapp_display}
                </a>
                <div className="mt-3 flex items-center gap-2 text-white/50 text-sm">
                  <MessageCircle size={14} /> {t(UI.contactWaHint)}
                </div>
              </div>

              <div className="border-t border-b border-white/10 py-8">
                <p className="num-tick text-white/50 mb-4">{t(UI.contactAreaLabel)}</p>
                <div className="flex flex-wrap gap-2">
                  {serviceArea.map((c) => (
                    <span
                      key={c}
                      className="text-xs font-mono-x uppercase tracking-widest text-white/70 border border-white/15 px-3 py-1.5 hover:border-[#1E7FFF] hover:text-white transition-colors"
                    >
                      {c}
                    </span>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-2 text-white/50 text-sm">
                  <MapPin size={14} /> {t(brand?.location)}
                </div>
              </div>
            </InView>
          </div>

          <div className="col-span-12 md:col-span-7">
            <InView delay={0.2}>
              <form
                onSubmit={submit}
                className="border border-white/10 p-6 md:p-10 bg-[#0d0d10]"
                data-testid="contact-form"
              >
                <p className="num-tick text-[#1E7FFF] mb-6">{t(UI.formTag)}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label={t(UI.formName)} testId="form-name">
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={update("name")}
                      className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#1E7FFF] focus:outline-none transition-colors font-body"
                      data-testid="contact-input-name"
                    />
                  </Field>
                  <Field label={t(UI.formPhone)} testId="form-phone">
                    <input
                      type="tel"
                      required
                      value={form.phone}
                      onChange={update("phone")}
                      className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#1E7FFF] focus:outline-none transition-colors font-body"
                      data-testid="contact-input-phone"
                    />
                  </Field>
                  <Field label={t(UI.formEmail)} testId="form-email">
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#1E7FFF] focus:outline-none transition-colors font-body"
                      data-testid="contact-input-email"
                    />
                  </Field>
                  <Field label={t(UI.formService)} testId="form-service">
                    <select
                      value={form.service}
                      onChange={update("service")}
                      className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#1E7FFF] focus:outline-none font-body"
                      data-testid="contact-input-service"
                    >
                      {services.map((s) => (
                        <option key={s.id} value={t(s.title)} className="bg-[#0d0d10]">
                          {t(s.title)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <div className="md:col-span-2">
                    <Field label={t(UI.formMessage)} testId="form-message">
                      <textarea
                        rows={4}
                        value={form.message}
                        onChange={update("message")}
                        className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#1E7FFF] focus:outline-none transition-colors resize-none font-body"
                        data-testid="contact-input-message"
                      />
                    </Field>
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                  <p className="num-tick text-white/40 max-w-sm">
                    {t(UI.formDisclaimer)}
                  </p>
                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileTap={{ scale: 0.97 }}
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="contact-submit"
                  >
                    <Send size={14} />
                    {loading ? t(UI.formSending) : t(UI.formSubmit)}
                  </motion.button>
                </div>
              </form>
            </InView>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="num-tick text-white/50 block mb-2">{label}</span>
      {children}
    </label>
  );
}
